import React, { useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  DialogContent,
  Grid,
  Stack,
  Button,
  IconButton,
  Box,
  useMediaQuery,
} from "@mui/material";
import CommonDialog from "../CommonDialog";
import TextFieldLabel from "../../TextFieldLabel";
import { CustomTextField } from "assets/styles/globalStyledComponent";
import {
  deleteRangeFromRoute,
  deleteStopFromRange,
  getAllRouteList,
  updateStopPriority,
} from "app/services/schoolService/transport.service";

import AddStopName from "./AddStopNameDialog";
import { Add, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import AddDistanceRange from "./AddDistanceRange";
import AddRouteDialog from "./AddRouteDialog";
import { toast } from "react-toastify";
import { queryClient } from "App";
import { useSelector } from "react-redux";
import { selectActiveSession } from "app/modules/schools/selectors";
import TableActionButtons from "../../tableComponent/TableActionButtons";
import DeleteDialog from "app/components/ConfirmationDialog/DeleteDialog";
import LinkFeeStructureDialog from "./LinkFeeStructureDialog";
import CustomLinearProgress from "../../CustomLinearProgress";

const ViewRouteDetail = ({
  open = false,
  routeData = {},
  vehicleList = [],
  onClose = () => {},
  refetch = () => {},
}) => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const selectedSession = useSelector(selectActiveSession);
  const [state, setState] = useState({
    routeDialog: false,
    routeData: {},
    stopDialog: false,
    stopData: {},
    rangeDialog: false,
    rangeData: {},
    deleteConfirmation: false,
    linkFeeStructureDialog: false,
    selectedFeeStructure: {},
  });
  // get All Route
  const {
    isLoading: getRouteDataByDocIdLoading,
    data: getRouteData,
    refetch: routeRefetch,
  } = useQuery({
    queryKey: ["getRouteDataByDocId"],
    queryFn: () => {
      let params = {
        routeDocId: routeData._id,
        session: selectedSession,
      };
      return getAllRouteList(params);
    },
    onSuccess: (isSuccess) => {},
    enabled: routeData._id ? true : false,
  });

  const handleDeleteStop = (stopDetail) => {
    deleteStopMutate(stopDetail._id);
  };

  const { mutate: deleteStopMutate, isLoading: deleteStopLoading } =
    useMutation("deleteStop", deleteStopFromRange, {
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(["getRouteDataByDocId"]);
        queryClient.invalidateQueries(["getAllStop"]);
        refetch();
        toast.success(data?.message);
        setState((prev) => ({
          ...prev,
          deleteConfirmation: false,
          stopData: {},
        }));
      },
      onError: (error) => {
        toast.error(error.response.data?.message);
      },
    });

  const {
    mutate: updateStopPriorityMutate,
    isLoading: updateStopPriorityLoading,
  } = useMutation("updateStopPriority", updateStopPriority, {
    onSuccess: ({ data }) => {
      routeRefetch();
      toast.success(data?.message);
    },
    onError: (error) => {
      toast.error(error.response.data?.message);
    },
  });

  const handleDeleteRange = (rangeId) => {
    deleteRangeMutate(rangeId);
  };

  const { mutate: deleteRangeMutate, isLoading: deleteRangeLoading } =
    useMutation(deleteRangeFromRoute, {
      onSuccess: ({ data }) => {
        queryClient.invalidateQueries(["getRouteDataByDocId"]);
        refetch();
        toast.success(data?.message);
        setState((prev) => ({
          ...prev,
          deleteRangeConfirmation: false,
          rangeToDelete: {},
        }));
      },
      onError: (error) => {
        toast.error(error.response.data?.message);
      },
    });

  return (
    <>
      <CustomLinearProgress
        loading={
          getRouteDataByDocIdLoading ||
          deleteStopLoading ||
          updateStopPriorityLoading
        }
      />
      <DeleteDialog
        open={state.deleteConfirmation}
        handleClose={() =>
          setState((prev) => ({
            ...prev,
            deleteConfirmation: false,
            stopData: {},
          }))
        }
        handleDelete={() => {
          handleDeleteStop(state.stopData);
        }}
        title={`${state?.stopData?.stopName ?? ""} Stop`}
      />
      <DeleteDialog
        open={state.deleteRangeConfirmation}
        handleClose={() =>
          setState((prev) => ({
            ...prev,
            deleteRangeConfirmation: false,
            rangeToDelete: {},
          }))
        }
        handleDelete={() => {
          handleDeleteRange(state.rangeToDelete._id);
        }}
        loading={deleteRangeLoading}
        title={`${state?.rangeToDelete?.rangeCode ?? ""} Range`}
        message={`Are you sure you want to delete the range ${state?.rangeToDelete?.rangeCode}?`}
      />

      <CommonDialog
        title={"Route Detail"}
        open={open}
        onClose={onClose}
        minWidth={{ xs: "100%", sm: "600px", md: "700px" }}
        maxWidth="md"
      >
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item sm={12} md={6}>
              <TextFieldLabel title="Route Number" />
              <CustomTextField
                disabled
                fullWidth
                size="small"
                value={getRouteData?.data?.obj?.routeNumber}
              />
            </Grid>

            <Grid item sm={12} md={6}>
              <TextFieldLabel title="Route Name" />
              <Stack
                direction={"row"}
                sx={{ alignItems: "center" }}
                spacing={1}
              >
                <CustomTextField
                  disabled
                  fullWidth
                  size="small"
                  value={getRouteData?.data?.obj?.routeName}
                />
                <TableActionButtons
                  showEdit={true}
                  handleEdit={() => {
                    setState((prev) => ({
                      ...prev,
                      routeDialog: true,
                      routeData: getRouteData?.data?.obj,
                    }));
                  }}
                />
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={2}>
            <Grid item xs={12}>
              <TextFieldLabel title={"Stop / Pickup Name"} mb={2} />

              {/* Desktop/Tablet View */}
              <Box sx={{ display: { xs: "none", md: "block", marginLeft: 2 } }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    background: "#F6F8FC",
                    fontWeight: 500,
                    fontSize: 14,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    border: "1px solid #B6C8D6",
                  }}
                >
                  <Grid item xs={1} sx={{ paddingLeft: 2 }}>
                    Range Code
                  </Grid>
                  <Grid item xs={2}>
                    Range
                  </Grid>
                  <Grid item xs={1}>
                    Delete
                  </Grid>
                  <Grid item xs={1} paddingLeft={2}>
                    Fee
                  </Grid>
                  <Grid item xs={2}>
                    Vehicle Number
                  </Grid>
                  <Grid item xs={2}>
                    Stop Name
                  </Grid>
                  <Grid item xs={2}>
                    Dist. From Sch.
                  </Grid>
                  <Grid item xs={1}>
                    Action
                  </Grid>
                </Grid>
                {getRouteData?.data?.obj?.rangeDetail?.map((range, index) => {
                  return (
                    <Grid
                      key={index}
                      container
                      mt={0}
                      spacing={2}
                      sx={{
                        padding: 1,
                        border: "1px solid #B6C8D6",
                        fontSize: 14,
                      }}
                      style={
                        getRouteData?.data?.obj?.rangeDetail?.length - 1 ===
                        index
                          ? {
                              borderBottomLeftRadius: 16,
                              borderBottomRightRadius: 16,
                            }
                          : {}
                      }
                    >
                      <Grid
                        item
                        xs={1}
                        sx={{
                          paddingLeft: 2,
                          cursor: "pointer",
                          color: "#2789FD",
                          textDecoration: "underline",
                        }}
                        onClick={() => {
                          setState((prev) => ({
                            ...prev,
                            rangeDialog: true,
                            rangeData: range,
                          }));
                        }}
                      >
                        {range?.rangeCode}
                      </Grid>

                      <Grid
                        item
                        xs={2}
                        sx={{
                          cursor: "pointer",
                          color: "#2789FD",
                          textDecoration: "underline",
                        }}
                        onClick={() => {
                          setState((prev) => ({
                            ...prev,
                            rangeDialog: true,
                            rangeData: range,
                          }));
                        }}
                      >
                        {range?.fromStopName} : ({range?.fromDistance} Km) to{" "}
                        {range?.toStopName} ({range.toDistance} Km)
                      </Grid>

                      <Grid item xs={1}>
                        <Box
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              deleteRangeConfirmation: true,
                              rangeToDelete: range,
                            }));
                          }}
                        >
                          <img
                            alt="delete"
                            src="/delete-icon.png"
                            className="action_icon"
                            height="20px"
                            width="20px"
                            style={{ objectFit: "contain" }}
                          />
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={1}
                        sx={{
                          cursor: "pointer",
                          paddingRight: 1,
                          fontSize: 12,
                          fontWeight: 400,
                          color: "#2789FD",
                          fontStyle: "italic",
                        }}
                      >
                        {range?.transportFeeDocId?.feeAmount ? (
                          <Button
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                linkFeeStructureDialog: true,
                                selectedFeeStructure: range,
                              }));
                            }}
                            title="Click to Edit Fee"
                            sx={{
                              background: "none",
                              color: "#2789FD",
                              textDecoration: "underline",
                              fontStyle: "italic",
                              fontSize: 12,
                            }}
                          >
                            {range?.transportFeeDocId?.feeAmount ?? 0}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                linkFeeStructureDialog: true,
                                selectedFeeStructure: range,
                              }));
                            }}
                            sx={{
                              background: "none",
                              color: "#2789FD",
                              textDecoration: "underline",
                              fontStyle: "italic",
                              fontSize: 12,
                              padding: 0,
                            }}
                          >
                            + Add Fee
                          </Button>
                        )}
                      </Grid>

                      <Grid item xs={2}>
                        {range?.transportDocId?.vehicleNumber}
                      </Grid>

                      <Grid container item xs={5}>
                        {range?.stopDetail?.map((stopDetail, index) => {
                          return (
                            <Grid
                              key={index}
                              container
                              sx={{
                                padding: 0.5,
                                alignItems: "center",
                                borderBottom: "0.5px solid #B6C8D6",
                                fontWeight: 500,
                              }}
                            >
                              <Grid
                                item
                                xs={5}
                                container
                                alignItems="center"
                                spacing={1}
                                p={1}
                              >
                                <IconButton
                                  onClick={() => {
                                    updateStopPriorityMutate({
                                      session: selectedSession,
                                      stopDocId: stopDetail._id,
                                      rangeDocId: range._id,
                                      order: "up",
                                    });
                                  }}
                                  sx={{
                                    padding: 0,
                                    color: "#2789FD",
                                  }}
                                  disabled={
                                    range?.stopDetail?.[0]?._id ===
                                    stopDetail?._id
                                  }
                                  title="Priority Up"
                                >
                                  <KeyboardArrowUp />
                                </IconButton>

                                <IconButton
                                  onClick={() => {
                                    updateStopPriorityMutate({
                                      session: selectedSession,
                                      stopDocId: stopDetail._id,
                                      rangeDocId: range._id,
                                      order: "down",
                                    });
                                  }}
                                  sx={{
                                    padding: 0,
                                    color: "#2789FD",
                                  }}
                                  title="Priority Down"
                                  disabled={
                                    range?.stopDetail?.[
                                      range?.stopDetail?.length - 1
                                    ]?._id === stopDetail?._id
                                  }
                                >
                                  <KeyboardArrowDown />
                                </IconButton>
                                {stopDetail?.stopName}
                              </Grid>

                              <Grid item xs={4}>
                                {stopDetail?.distance}
                              </Grid>
                              <Grid item xs={2}>
                                <TableActionButtons
                                  showDelete={true}
                                  handleDelete={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      deleteConfirmation: true,
                                      stopData: stopDetail,
                                    }));
                                  }}
                                  showEdit={true}
                                  handleEdit={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      stopDialog: true,
                                      stopData: stopDetail,
                                    }));
                                  }}
                                />
                              </Grid>
                            </Grid>
                          );
                        })}
                        <Button
                          variant="outlined"
                          size="small"
                          color="secondary"
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              stopDialog: true,
                              stopData: {
                                rangeDocId: range._id,
                              },
                            }));
                          }}
                          sx={{
                            marginTop: 1,
                          }}
                        >
                          + Add More Stop
                        </Button>
                      </Grid>
                    </Grid>
                  );
                })}
              </Box>

              {/* Mobile View */}
              <Box sx={{ display: { xs: "block", md: "none" } }}>
                {getRouteData?.data?.obj?.rangeDetail?.map((range, index) => {
                  return (
                    <Box
                      key={range._id}
                      sx={{
                        border: "1px solid #B6C8D6",
                        borderRadius: 2,
                        padding: 2,
                        marginBottom: 2,
                        backgroundColor: "#FFFFFF",
                      }}
                    >
                      {/* Range Header */}
                      <Stack spacing={1} mb={2}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              cursor: "pointer",
                              color: "#2789FD",
                              textDecoration: "underline",
                              fontWeight: 500,
                            }}
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                rangeDialog: true,
                                rangeData: range,
                              }));
                            }}
                          >
                            Range Code: {range?.rangeCode}
                          </Box>
                          <Box
                            sx={{ cursor: "pointer" }}
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                deleteRangeConfirmation: true,
                                rangeToDelete: range,
                              }));
                            }}
                          >
                            <img
                              alt="delete"
                              src="/delete-icon.png"
                              className="action_icon"
                              height="20px"
                              width="20px"
                              style={{ objectFit: "contain" }}
                            />
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            cursor: "pointer",
                            color: "#2789FD",
                            textDecoration: "underline",
                            fontSize: 14,
                          }}
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              rangeDialog: true,
                              rangeData: range,
                            }));
                          }}
                        >
                          Range: {range?.fromStopName} ({range?.fromDistance}{" "}
                          Km) to {range?.toStopName} ({range.toDistance} Km)
                        </Box>

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            {range?.transportFeeDocId?.feeAmount ? (
                              <Button
                                onClick={() => {
                                  setState((prev) => ({
                                    ...prev,
                                    linkFeeStructureDialog: true,
                                    selectedFeeStructure: range,
                                  }));
                                }}
                                title="Click to Edit Fee"
                                size="small"
                                sx={{
                                  background: "none",
                                  color: "#2789FD",
                                  textDecoration: "underline",
                                  fontStyle: "italic",
                                  fontSize: 12,
                                  padding: 0,
                                }}
                              >
                                Fee: ₹{range?.transportFeeDocId?.feeAmount ?? 0}
                              </Button>
                            ) : (
                              <Button
                                onClick={() => {
                                  setState((prev) => ({
                                    ...prev,
                                    linkFeeStructureDialog: true,
                                    selectedFeeStructure: range,
                                  }));
                                }}
                                size="small"
                                sx={{
                                  background: "none",
                                  color: "#2789FD",
                                  textDecoration: "underline",
                                  fontStyle: "italic",
                                  fontSize: 12,
                                  padding: 0,
                                }}
                              >
                                + Add Fee
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ fontSize: 14 }}>
                            Vehicle:{" "}
                            {range?.transportDocId?.vehicleNumber || "N/A"}
                          </Box>
                        </Stack>
                      </Stack>

                      {/* Stops List */}
                      <Box
                        sx={{ borderTop: "1px solid #E0E0E0", paddingTop: 2 }}
                      >
                        <Box
                          sx={{
                            fontWeight: 500,
                            marginBottom: 1,
                            fontSize: 14,
                          }}
                        >
                          Stops:
                        </Box>
                        {range?.stopDetail?.map((stopDetail, stopIndex) => {
                          return (
                            <Box
                              key={stopDetail._id}
                              sx={{
                                padding: 1,
                                marginBottom: 1,
                                backgroundColor: "#F6F8FC",
                                borderRadius: 1,
                                border: "1px solid #E0E0E0",
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <Stack spacing={0.5} flex={1}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                  >
                                    <IconButton
                                      onClick={() => {
                                        updateStopPriorityMutate({
                                          session: selectedSession,
                                          stopDocId: stopDetail._id,
                                          rangeDocId: range._id,
                                          order: "up",
                                        });
                                      }}
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        color: "#2789FD",
                                      }}
                                      disabled={
                                        range?.stopDetail?.[0]?._id ===
                                        stopDetail?._id
                                      }
                                      title="Priority Up"
                                    >
                                      <KeyboardArrowUp fontSize="small" />
                                    </IconButton>

                                    <IconButton
                                      onClick={() => {
                                        updateStopPriorityMutate({
                                          session: selectedSession,
                                          stopDocId: stopDetail._id,
                                          rangeDocId: range._id,
                                          order: "down",
                                        });
                                      }}
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        color: "#2789FD",
                                      }}
                                      title="Priority Down"
                                      disabled={
                                        range?.stopDetail?.[
                                          range?.stopDetail?.length - 1
                                        ]?._id === stopDetail?._id
                                      }
                                    >
                                      <KeyboardArrowDown fontSize="small" />
                                    </IconButton>
                                    <Box sx={{ fontWeight: 500, fontSize: 14 }}>
                                      {stopDetail?.stopName}
                                    </Box>
                                  </Stack>
                                  <Box
                                    sx={{
                                      fontSize: 13,
                                      color: "#666",
                                      marginLeft: 4,
                                    }}
                                  >
                                    Distance From Sch.: {stopDetail?.distance}
                                  </Box>
                                </Stack>

                                <TableActionButtons
                                  showDelete={true}
                                  handleDelete={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      deleteConfirmation: true,
                                      stopData: stopDetail,
                                    }));
                                  }}
                                  showEdit={true}
                                  handleEdit={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      stopDialog: true,
                                      stopData: stopDetail,
                                    }));
                                  }}
                                />
                              </Stack>
                            </Box>
                          );
                        })}
                        <Button
                          onClick={() => {
                            setState((prev) => ({
                              ...prev,
                              stopDialog: true,
                              stopData: {
                                rangeDocId: range._id,
                              },
                            }));
                          }}
                          fullWidth
                          sx={{
                            background: "none",
                            color: "#2789FD",
                            fontStyle: "italic",
                            fontSize: 12,
                            marginTop: 1,
                            border: "1px dashed #2789FD",
                          }}
                        >
                          + Add More Stop
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>

          <Button
            sx={{ marginTop: isMobile ? 1 : 2 }}
            variant="outlined"
            color="secondary"
            size="medium"
            onClick={() => {
              setState((prev) => ({
                ...prev,
                rangeDialog: true,
              }));
            }}
            startIcon={<Add />}
          >
            Add More Range
          </Button>
        </DialogContent>
      </CommonDialog>

      <LinkFeeStructureDialog
        open={state.linkFeeStructureDialog}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            linkFeeStructureDialog: false,
            selectedFeeStructure: {},
          }))
        }
        refetch={() => {
          routeRefetch();
          refetch();
        }}
        selectedFeeStructure={state.selectedFeeStructure}
      />

      {/* Update Route Dialog */}
      {state.routeDialog && (
        <AddRouteDialog
          open={state.routeDialog}
          routeData={state.routeData}
          onClose={() =>
            setState((prev) => ({
              ...prev,
              routeDialog: false,
              routeData: {},
            }))
          }
          refetch={refetch}
        />
      )}

      {/* Create/Update Stop */}
      {state.stopDialog && (
        <AddStopName
          open={state.stopDialog}
          stopData={state.stopData}
          routeData={routeData}
          onClose={() => {
            setState((prev) => ({
              ...prev,
              stopDialog: false,
              stopData: {},
            }));
          }}
          refetch={() => {
            routeRefetch();
            refetch();
          }}
        />
      )}

      {/* Create/Update Range */}
      {state.rangeDialog && (
        <AddDistanceRange
          open={state.rangeDialog}
          rangeData={state.rangeData}
          routeData={routeData}
          vehicleList={vehicleList}
          onClose={() => {
            setState((prev) => ({
              ...prev,
              rangeDialog: false,
              rangeData: {},
            }));
          }}
        />
      )}
    </>
  );
};

export default ViewRouteDetail;
